import { Injectable, inject } from '@angular/core';
import {
  AssetService,
  MachineGroupInfo,
  MachineInfo,
  PlantInfo,
  SpinnerV2Service,
  StorageV2Service,
  StoreV2,
  ToolingInfo,
} from '@schenck/rotec-components';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { objContainsFilterStr } from 'src/utils';

export interface TileViewState {
  order: string[];
  filter: string;
  selectedPlant: string;
  selectedGroup: string;
}

@Injectable()
export class TileViewService extends StoreV2<TileViewState> {
  private readonly assetService = inject(AssetService);
  private readonly loadingService = inject(SpinnerV2Service);

  selectedPlant$: Observable<string>;
  selectedGroup$: Observable<string>;

  visibleMachines$: Observable<MachineInfo[]>;
  visibleToolings$: Observable<ToolingInfo[]>;
  tiles$: Observable<
    (
      | ({
          _assetType: string;
          plantName: string;
          machineGroups: string;
        } & MachineInfo)
      | ({
          _assetType: string;
          machines: string;
        } & ToolingInfo)
    )[]
  >;

  constructor() {
    const storageService = inject(StorageV2Service);

    super(
      {
        order: [],
        filter: '',
        selectedPlant: '',
        selectedGroup: '',
      },
      'TileViewService',
      { storageService, name: 'TileViewService' }
    );

    this.loadingService.setLoading('assets', true);

    this.selectedPlant$ = this.state$.pipe(map((state) => state.selectedPlant));
    this.selectedGroup$ = this.state$.pipe(map((state) => state.selectedGroup));

    this.visibleMachines$ = combineLatest([this.selectedPlant$, this.selectedGroup$]).pipe(
      switchMap(([selectedPlant, selectedGroup]) => {
        if (!selectedGroup && !selectedPlant) {
          return this.assetService.machines$;
        } else if (selectedPlant && !selectedGroup) {
          return this.assetService.machinesInsidePlant$(selectedPlant);
        } else if (selectedGroup && !selectedPlant) {
          return this.assetService.machinesInsideGroup$(selectedGroup);
        } else {
          return this.assetService
            .machinesInsideGroup$(selectedGroup)
            .pipe(map((machines) => machines.filter(({ plantId }) => plantId === selectedPlant)));
        }
      })
    );

    this.visibleToolings$ = this.assetService.toolingsOfMachines$(this.visibleMachines$);

    this.tiles$ = combineLatest([
      this.visibleMachines$,
      this.visibleToolings$,
      this.assetService.plants$,
      this.assetService.machineGroups$,
      this.state$,
    ]).pipe(
      map(([machines, toolings, plants, machineGroups, state]) => {
        const tiles = [
          ...machines
            .filter((machine) => !machine.isDeactivated)
            .map((m) => mapMachine(m, plants, machineGroups)),
          ...toolings
            .filter((tooling) => !tooling.isDeactivated)
            .map((t) => mapTooling(t, machines)),
        ].sort((a, b) => {
          if (state.order.indexOf(a.id) < state.order.indexOf(b.id)) {
            return -1;
          }
          if (state.order.indexOf(a.id) > state.order.indexOf(b.id)) {
            return 1;
          }
          return 0;
        });
        this.loadingService.setLoading('assets', false);
        if (!!state.filter) {
          return tiles.filter((tile) => objContainsFilterStr(tile, state.filter));
        } else {
          return tiles;
        }
      })
    );
  }

  updateOrder(order: string[]) {
    this.patchState('updated tile order', { order: order });
  }

  filterTiles(filter: string) {
    this.patchState('filter tiles', { filter: filter });
  }

  filterForPlant(selectedPlant: string) {
    const selectedGroup =
      this.assetService
        .machineGroupsByPlantId(selectedPlant)
        .find(({ id }) => this.state.selectedGroup === id)?.id || '';

    this.patchState('filter for plant', { selectedPlant, selectedGroup });
  }

  filterForGroup(selectedGroup: string) {
    const selectedPlant =
      this.assetService
        .plantsByMachineGroupId(selectedGroup)
        .find(({ id }) => this.state.selectedPlant === id)?.id || '';

    this.patchState('filter for group', { selectedGroup, selectedPlant });
  }
}

function mapTooling(
  t: ToolingInfo,
  machines: MachineInfo[]
): {
  _assetType: string;
  machines: string;
} & ToolingInfo {
  return {
    ...t,
    _assetType: 'tooling',
    machines: machines
      .filter((machine) => t.machineIds.includes(machine.id) && !machine.isDeactivated)
      .map((machine) => machine.name)
      .join(','),
  };
}

function mapMachine(
  m: MachineInfo,
  plants: PlantInfo[],
  machineGroups: MachineGroupInfo[]
): {
  _assetType: string;
  plantName: string;
  machineGroups: string;
} & MachineInfo {
  return {
    ...m,
    _assetType: 'machine',
    plantName: plants.find((plant) => plant.id === m.plantId).name,
    machineGroups: machineGroups
      .filter((machineGroup) => machineGroup.childMachineIds.includes(m.id))
      .map((machineGroup) => machineGroup.name)
      .join(','),
  };
}
